import React, { useMemo, useState } from "react";
import panelStyles from "./IdEditor.module.css";
import boxStyles from "../Editor.module.css";
import { ID } from "../../shared/types";

interface Props {
  ids: ID[];
  onAdd: (id: ID) => void;
  onSelect: (id: ID) => void;
  onRemove: (id: ID) => void;
  onClose: () => void;
  sandbox: boolean;
  usedIds?: Set<ID>;
}

const IdSelectorPanel: React.FC<Props> = ({
  ids,
  onAdd,
  onSelect,
  onRemove,
  onClose,
  sandbox,
  usedIds = new Set(),
}) => {
  const nextId = useMemo<number>(() => {
    const nums = ids.filter((v): v is number => typeof v === "number");
    let i = 1;
    while (nums.includes(i)) i += 1;
    return i;
  }, [ids]);

  const [customId, setCustomId] = useState("");
  const [showWarn, setShowWarn] = useState(false);
  const [showDup, setShowDup] = useState(false);

  const handleAdd = () => {
    if (customId.trim() !== "") {
      const n = Number(customId);
      if (Number.isInteger(n)) {
        if (ids.includes(n as ID)) {
          setShowDup(true);
          setShowWarn(false);
          return;
        }
        onAdd(n as ID);
        setCustomId("");
        setShowWarn(false);
        setShowDup(false);
        return;
      }
      setShowWarn(true);
      setShowDup(false);
      return;
    }
    onAdd(nextId);
    setShowWarn(false);
    setShowDup(false);
  };
  return (
    <div data-tour="reference-picker"
      className={`${boxStyles.boxEditorModule} ${panelStyles.panelShell} ${panelStyles.activeOutline}`}
    >
      <div className={`drag-handle ${panelStyles.header}`}>
        <span>Select ID</span>
        <button className={boxStyles.removeItem} onClick={onClose} title="Close">
          ×
        </button>
      </div>

      <div className={panelStyles.content}>
        <div className={boxStyles.collectionIdContainer}>
          {ids.map((id) => {
            const isUsed = usedIds.has(id);
            return (
              <div key={id.toString()} className={boxStyles.collectionIdBox}>
                <button
                  type="button"
                  className={`${boxStyles.collectionIdNoBorder} ${
                    !isUsed ? panelStyles.unusedId : ""
                  }`}
                  onClick={() => onSelect(id)}
                  title={!isUsed ? "This ID is currently unused" : ""}
                >
                  {id}
                </button>
                {sandbox && (
                  <button
                    type="button"
                    className={boxStyles.collectionRemoveId}
                    onClick={() => onRemove(id)}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {sandbox && ids.length === 0 && (
          <div className={panelStyles.empty}>
            No IDs yet — click “Add ID” to create one.
          </div>
        )}

        <div className={panelStyles.panelControlsDiv}>
          {sandbox && (
            <>
              <input
                type="text"
                value={customId}
                onChange={(e) => {
                  setCustomId(e.target.value);
                  setShowWarn(false);
                  setShowDup(false);
                }}
                placeholder="Enter custom ID or leave blank"
                className={panelStyles.idInputBox}
              />
              <button
                type="button"
                onClick={handleAdd}
                className={boxStyles.addButton}
              >
                Add ID
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => onSelect("_")}
            className={boxStyles.removeButton}
          >
            Unassign
          </button>
        </div>

        {showWarn && (
          <span style={{ color: "#dc2626", fontSize: "0.8rem" }}>
            Please enter an integer
          </span>
        )}
        {showDup && (
          <span style={{ color: "#dc2626", fontSize: "0.8rem" }}>
            Already added
          </span>
        )}
      </div>
    </div>
  );
};

export default IdSelectorPanel;
